import '../../dashboard/dashboard-reset.css';
import { TopLevelOrderStatus } from '../../../../api/generated-catch-backend/model';

interface ProgressStep {
  title: string;
  description: string;
  completed: boolean;
}

interface LabsProgressTrackerProps {
  status?: TopLevelOrderStatus;
  videoSrc?: string;
}

const getProgressSteps = (status?: TopLevelOrderStatus): ProgressStep[] => {
  // Step 1: Order placed (completed when not eligible)
  const orderPlaced = status !== undefined && status !== TopLevelOrderStatus.eligible;

  // Step 2: Labs in progress (completed when order is placed and ready for testing)
  const labsInProgress =
    status === TopLevelOrderStatus.ready_for_testing ||
    status === TopLevelOrderStatus.pending_review ||
    status === TopLevelOrderStatus.results_ready;

  // Step 3: Results ready (completed when results are available)
  const resultsReady = status === TopLevelOrderStatus.results_ready;

  return [
    {
      title: 'Order Labs',
      description: "You've ordered your annual blood panel.",
      completed: orderPlaced,
    },
    {
      title: 'Labs In Progress',
      description:
        "If you haven't gone yet, head to the lab you selected for your blood draw. If you've already gone, your sample is being reviewed by a physician.",
      completed: labsInProgress,
    },
    {
      title: 'Review Results',
      description:
        "You'll get a clinician-reviewed report and updated cancer risk analysis based on 50+ biomarkers",
      completed: resultsReady,
    },
  ];
};

export const LabsProgressTracker = ({ status, videoSrc = '/videos/catch-hero.mp4' }: LabsProgressTrackerProps) => {
  const progressSteps = getProgressSteps(status);

  return (
    <div className="dashboard-reset relative w-full self-start overflow-hidden rounded-[10px] bg-[#183344]">
      {/* Background Video */}
      <video
        src={videoSrc}
        autoPlay
        loop
        muted
        playsInline
        className="absolute left-0 top-0 z-0 h-full w-full object-cover"
      />

      {/* Dark overlay */}
      <div className="absolute left-0 top-0 z-[1] h-full w-full bg-black/65" />

      {/* Content */}
      <div className="relative z-10 flex h-full flex-col gap-6 p-8">
        {/* Title */}
        <h3 className="text-center text-[22px] font-bold leading-[1.3] tracking-[0] text-white">
          Your Labs Are In Progress
        </h3>

        {/* Progress Steps */}
        <div className="flex flex-col gap-4">
          {progressSteps.map((step, index) => {
            const isLastStep = index === progressSteps.length - 1;
            const nextStepCompleted = !isLastStep && progressSteps[index + 1].completed;
            const lineColor = step.completed && nextStepCompleted ? '#7CF1C7' : 'rgba(255, 255, 255, 0.2)';

            return (
              <div key={index} className="flex gap-3">
                {/* Icon Column with Line Below */}
                <div className="flex flex-col items-center self-stretch">
                  {/* Checkmark or Circle */}
                  <div className="relative z-10 flex-shrink-0">
                    {step.completed ? (
                      <div className="flex h-6 w-6 items-center justify-center rounded-full bg-[#7CF1C7]">
                        <svg
                          width="12"
                          height="10"
                          viewBox="0 0 12 10"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          className="text-catch-dark-blue-90"
                        >
                          <path
                            d="M1 5L4.5 8.5L11 1"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                    ) : (
                      <div className="h-6 w-6 rounded-full border-2 border-white/40" />
                    )}
                  </div>

                  {/* Connecting Line */}
                  {!isLastStep && (
                    <div
                      className="mb-[-1rem] w-0.5 flex-1"
                      style={{ backgroundColor: lineColor }}
                    />
                  )}
                </div>

                {/* Text Content */}
                <div className="flex-1 pt-0.5">
                  <h4 className="text-base font-bold leading-tight text-white">{step.title}</h4>
                  <p className="mt-1 text-sm font-normal leading-relaxed text-white/90">{step.description}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
